import React, { useState } from "react"

import { SettingRow, Tabs, TabsList, TabsTrigger, Textarea } from "@coval/ui"

import { JsonCodeEditor } from "@/components/JsonCodeEditor"

type TextConfigurationMode = "text" | "json"

function looksLikeJson(value: string): boolean {
  const trimmed = value.trim()
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
    return false
  }
  try {
    JSON.parse(trimmed)
    return true
  } catch {
    return false
  }
}

/**
 * Free-form persona data with a String / JSON switch. JSON mode swaps in the
 * shared CodeMirror editor so structured data gets highlighting and linting;
 * the stored value is the same string either way.
 */
export function PersonaTextConfigurationField({
  value,
  onChange,
  disabled,
}: {
  value: string
  onChange: (value: string) => void
  disabled: boolean
}) {
  const [mode, setMode] = useState<TextConfigurationMode>(() =>
    looksLikeJson(value) ? "json" : "text",
  )

  return (
    <SettingRow
      label="Text Configuration"
      tooltip="Additional data or context specific to this persona. Choose JSON for structured data and get syntax checking."
      stacked
      action={
        <Tabs
          value={mode}
          onValueChange={(next) => setMode(next as TextConfigurationMode)}
        >
          <TabsList variant="pill" aria-label="Text configuration format">
            <TabsTrigger
              value="text"
              variant="pill"
              className="px-3"
              disabled={disabled}
            >
              String
            </TabsTrigger>
            <TabsTrigger
              value="json"
              variant="pill"
              className="px-3"
              disabled={disabled}
            >
              JSON
            </TabsTrigger>
          </TabsList>
        </Tabs>
      }
    >
      {/* Both start at four lines. The JSON editor then grows with its content
          up to a ceiling, so it never shows empty space below the last line. */}
      {(controlId) =>
        mode === "json" ? (
          // The editor is a client-only chunk, so on its first mount it renders
          // nothing for a beat. Reserving four lines of height keeps the page
          // from collapsing and jumping the scroll while it loads.
          <div className="min-h-[90px]">
            <JsonCodeEditor
              ariaLabel="Text Configuration"
              value={value}
              onChange={onChange}
              placeholder='{ "account_tier": "premium" }'
              rows={4}
              maxHeight="320px"
              disabled={disabled}
            />
          </div>
        ) : (
          <Textarea
            id={controlId}
            aria-label="Text Configuration"
            className="resize-none"
            rows={4}
            placeholder="Enter additional context or metadata for the persona"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            disabled={disabled}
          />
        )
      }
    </SettingRow>
  )
}
